/**
 * Invalid or missing token state.
 * Focus management for WCAG 2.4.3 compliance.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { AlertTriangle } from "lucide-react";
import type { TokenError } from "~/features/onboarding/useOnboardingToken";

interface InvalidTokenStateProps {
  error: TokenError | null;
}

export function InvalidTokenState({ error }: InvalidTokenStateProps) {
  const { t } = useTranslation();
  const isUsed = error === "used";
  const headingRef = React.useRef<HTMLHeadingElement>(null);

  // Focus on heading when component mounts for screen reader users
  React.useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="w-full py-6 sm:py-12 px-0 sm:px-4">
      <div className="mx-auto max-w-lg">
        <Card className="border-destructive/50 rounded-none sm:rounded-lg border-x-0 sm:border-x">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10" aria-hidden="true">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle ref={headingRef} tabIndex={-1} as="h1" className="text-2xl text-destructive outline-none">
              {isUsed
                ? t("onboarding.token.usedTitle")
                : t("onboarding.token.invalidTitle")}
            </CardTitle>
            <CardDescription>
              {error === "missing"
                ? t("onboarding.token.missingDescription")
                : isUsed
                  ? t("onboarding.token.usedDescription")
                  : t("onboarding.token.invalidDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-muted p-4">
              <p className="text-sm text-muted-foreground">
                {t("onboarding.token.requestNewLink")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
